import { Product } from '@portal/portal-core';
import React from 'react';
import styled, { withTheme } from 'styled-components';
import type { DefaultTheme } from 'styled-components';

import CustomFieldsForm from 'components/custom-fields-form';
import { FjLinkButton, FjModal, FjNotification } from 'components/primitives';
import { sortFieldsUserForm } from 'ducks/custom-fields/utils';
import { APPLICATION_NAME, isSuperAdmin, routeIsAdmin, urlParameterValues } from 'utils';

const { Notification, Manager, ICONS } = FjNotification;

// Notification's own props type omits `id`, but the parent Manager reads `id`
// off each child element at runtime, so we widen the accepted props to include it.
const NotificationWithId = Notification as unknown as React.ComponentType<
  React.ComponentProps<typeof Notification> & { id?: string }
>;

declare global {
  interface Window {
    dismissSuperAdminAlert?: boolean;
  }
}

interface Invite {
  costCenterName: string;
  costCenterId: string;
  productAccessName: string;
  productAccessId: string;
  token: string;
  userId: string;
  id: string;
}

interface CustomFieldsFormInfo {
  userId: string;
  costCenterId: string;
  productAccessId: string;
  keys: Parameters<typeof sortFieldsUserForm>[0];
  costCenterName: string;
  productName: string;
}

interface InviteAcceptanceState {
  open: boolean;
  success: boolean;
  result: unknown;
}

interface SuperAdminCostCenter {
  name: string;
}

// The child form's customFields prop expects this minimal shape; sortFieldsUserForm
// returns a looser CustomFieldForm shape, so we cast at the boundary.
interface CustomFieldItem {
  id: string;
  label: string;
  required?: boolean;
}

interface NotificationsProps {
  theme: DefaultTheme;
  authenticated: boolean;
  invites?: Invite[];
  superAdminCostCenter?: SuperAdminCostCenter | null;
  customFieldsFormInfo?: CustomFieldsFormInfo;
  inviteAcceptanceState?: InviteAcceptanceState;
  acceptInvite: (userId: string, id: string) => void;
  dismissInvite: (token: string) => void;
  dismissInviteConfirmation: () => void;
  onSubmitCustomFields: (...args: unknown[]) => void;
  customFieldsSubmitFailure?: { error?: unknown; [key: string]: unknown };
}

interface NotificationsState {
  acceptedInviteProductName: string | null;
  dismissSuperAdminAlert: boolean;
}

export class Notifications extends React.Component<NotificationsProps, NotificationsState> {
  constructor(props: NotificationsProps) {
    super(props);
    this.state = {
      acceptedInviteProductName: null,
      dismissSuperAdminAlert: Boolean(window.dismissSuperAdminAlert),
    };
  }

  getParams = (): Record<string, string> => urlParameterValues();

  isCreateUser = (): boolean =>
    Object.keys(this.getParams()).includes('CREATE_NEW_USER_SUCCESS');

  get notificationStyle(): { primaryColor: string; bgColor: string; height: string } {
    return {
      primaryColor: this.props.theme.colors.alert,
      bgColor: this.props.theme.colors.white,
      height: '85px',
    };
  }

  get superAdminNotificationStyle(): { primaryColor: string; bgColor: string; height: string } {
    return {
      ...this.notificationStyle,
      height: '70px',
    };
  }

  renderSuperAdminSiteViewAlert(): React.ReactNode {
    const isSuperAdminSiteView = isSuperAdmin() && routeIsAdmin();
    if (!isSuperAdminSiteView) {
      return null;
    }
    // TODO move Cheap Dismissal Mechanism to store for persistance. needed for testing.
    // Cheap dismissal mechanism: stash on `window`, since we don't care about persisting across full page reloads
    if (this.state.dismissSuperAdminAlert || !this.props.superAdminCostCenter) {
      return null;
    }

    return (
      <StyledManager width="700px">
        <NotificationWithId
          message={
            <span>You are viewing {this.props.superAdminCostCenter.name} as a site admin.</span>
          }
          key="superAdminSiteAlert"
          id="superAdminSiteAlert"
          handleClose={() => {
            window.dismissSuperAdminAlert = true;
            this.setState({ dismissSuperAdminAlert: true });
          }}
          style={this.superAdminNotificationStyle}
        />
      </StyledManager>
    );
  }

  // if the productAccess name is not flowjo or seqgeq, it should default to ICONS.NOTIFICATION_EMAIL
  getProductIcon = (productAccessName: string): string => {
    const iconsByName: Record<string, string> = {
      flowjo: ICONS.NOTIFICATION_FLOWJO,
      seqgeq: ICONS.NOTIFICATION_SEQGEQ,
    };
    return iconsByName[productAccessName.toLowerCase()] || ICONS.NOTIFICATION_EMAIL;
  };

  renderInvites(): React.ReactNode {
    const { authenticated, invites, customFieldsFormInfo } = this.props;

    if (!authenticated) {
      return null;
    }
    if (invites?.length && customFieldsFormInfo) {
      const { userId, costCenterId, productAccessId, keys, costCenterName, productName } =
        customFieldsFormInfo;

      let pendingInvite: Partial<Invite> = {};
      invites.forEach((invite) => {
        if (invite.productAccessId === productAccessId) {
          pendingInvite = invite;
        }
      });

      return (
        <CustomFieldsForm
          userId={userId}
          costCenterId={costCenterId}
          productAccessId={productAccessId}
          customFields={sortFieldsUserForm(keys) as unknown as CustomFieldItem[]}
          mode="live"
          customFieldsAction={(...args: unknown[]) => {
            // the confirmation modal names the product, as when an invite link is clicked
            this.setState({ acceptedInviteProductName: productName });
            this.props.onSubmitCustomFields(...args);
          }}
          costCenterName={costCenterName}
          productName={productName}
          inviteId={pendingInvite.id}
          submitFailure={this.props.customFieldsSubmitFailure}
        />
      );
    }
    if (invites?.length) {
      return (
        <StyledManager width="700px">
          {invites.map((invite) => (
            <NotificationWithId
              message={
                <span>
                  You are invited to join the {invite.costCenterName}{' '}
                  {Product.PRODUCT_DISPLAY_NAMES[invite.productAccessName]} license.{' '}
                  <FjLinkButton
                    onClick={() => {
                      this.setState({ acceptedInviteProductName: invite.productAccessName });
                      this.props.acceptInvite(invite.userId, invite.id);
                    }}
                  >
                    Please click here to confirm you accept the invitation to join this site
                    license.
                  </FjLinkButton>
                </span>
              }
              icon={this.getProductIcon(invite.productAccessName)}
              key={invite.id}
              id={invite.id}
              handleClose={() => this.props.dismissInvite(invite.token)}
              style={this.notificationStyle}
            />
          ))}
        </StyledManager>
      );
    }
    return null;
  }

  renderInviteConfirmationModal(): React.ReactNode {
    const {
      customFieldsFormInfo,
      authenticated,
      inviteAcceptanceState,
      dismissInviteConfirmation,
    } = this.props;

    // Use the stored productAccessName from the accepted invite, or empty string if not available
    const productDisplayName = this.state.acceptedInviteProductName
      ? Product.PRODUCT_DISPLAY_NAMES[this.state.acceptedInviteProductName] || ''
      : '';

    if (authenticated && !customFieldsFormInfo && inviteAcceptanceState?.open) {
      let message: string;

      const { success, result } = inviteAcceptanceState;

      if (success) {
        message = `Hoorah! You now have an active license and can begin logging into the${
          productDisplayName ? ` ${productDisplayName}` : ''
        } application using your ${APPLICATION_NAME} account username and password.`;
      } else if (result) {
        // can we trust result will always be a string or an object?  better to convert to a string either way
        message = JSON.stringify(result);
      } else {
        message = 'Unrecognized result from server.';
      }

      return (
        <FjModal
          accept={{
            text: 'OK',
            callback: () => {
              dismissInviteConfirmation();
              // Clear the stored product name when dismissing
              this.setState({ acceptedInviteProductName: null });
              if (this.isCreateUser()) {
                window.history.replaceState({}, document.title, '/');
              }
            },
          }}
        >
          {message}
        </FjModal>
      );
    }
    return null;
  }

  render(): React.ReactNode {
    return (
      <React.Fragment>
        {this.renderInviteConfirmationModal()}
        {this.renderInvites()}
        {this.renderSuperAdminSiteViewAlert()}
      </React.Fragment>
    );
  }
}

export default withTheme(Notifications);

const StyledManager = styled(Manager)`
  top: 120px;
  z-index: 99;
`;
